/**
 * Cobro a varios alumnos de una sola vez.
 *
 * El caso es de ventanilla y ya se veía en los datos: el mismo encargado paga
 * por tres hijos, y caja tenía que abrir tres fichas, cobrar tres veces y luego
 * ir a otra pantalla a unir los tres comprobantes en uno. Aquí se eligen los
 * alumnos, se marca lo de cada uno, y se cobra UNA vez.
 *
 * Lo que NO cambia, y es deliberado: cada alumno conserva su propio pago, con su
 * consecutivo y su efecto sobre SU saldo. Un pago único para tres hermanos
 * dejaría sin respuesta la pregunta más frecuente de la ventanilla —«¿cuánto
 * debe este?»— y haría imposible su estado de cuenta. Lo único que se une es el
 * papel que se entrega.
 *
 * No hay parentesco en el sistema: los alumnos se eligen a mano. Sirve igual
 * para hermanos con apellidos distintos, para un patrocinador o para un tío que
 * paga por dos sobrinos.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DESCUENTO_ANUAL_HERMANOS,
  DomainError,
  formatMoney,
  fromRow,
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
} from "@senm/domain";
import type { ReceiptData } from "@senm/contracts";
import {
  db,
  normalizeForSearch,
  type LocalCharge,
  type LocalStudent,
} from "../local/db.js";
import {
  openPdf,
  registerPayment,
  renderConsolidatedLocalReceipt,
} from "../services/payments.js";
import {
  descuentoAnualDisponible,
  distribucionExacta,
  estadosDeMora,
  etiquetasDeMes,
} from "../services/cobroDesdeCalendario.js";
import {
  SELECCION_VACIA,
  StudentCalendar,
  type SeleccionCalendario,
} from "./Calendar.js";
import type { StoredSession } from "../local/session.js";

const METODOS: PaymentMethod[] = [
  "EFECTIVO",
  "TRANSFERENCIA",
  "DEPOSITO",
  "CHEQUE",
];

/** Un alumno dentro del cobro, con lo que se le marcó. */
interface Renglon {
  alumno: LocalStudent;
  seleccion: SeleccionCalendario;
}

/** Resultado de intentar cobrarle a un alumno. */
interface Resultado {
  alumno: string;
  paymentId: string | null;
  /** Datos del recibo, para armar el comprobante conjunto sin pedirlo al servidor. */
  recibo: ReceiptData | null;
  problema: string | null;
}

export function CobroFamiliarPage(props: {
  session: StoredSession;
  conexion: string;
  onCambio: () => Promise<void>;
  tickSync: number;
}): JSX.Element {
  const [renglones, setRenglones] = useState<Renglon[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [sugerencias, setSugerencias] = useState<LocalStudent[]>([]);

  const [metodo, setMetodo] = useState<PaymentMethod>("EFECTIVO");
  const [referencia, setReferencia] = useState("");
  const [nota, setNota] = useState("");
  /*
   * Las mismas dos fechas que el cobro de un alumno.
   *
   * Aquí se tomaba siempre la de hoy, sin preguntar: un depósito del viernes
   * que se captura el lunes salía fechado el lunes, y en el cobro individual sí
   * se podía corregir. No hay motivo para que cobrar a dos hermanos ofrezca
   * menos que cobrarle a uno.
   */
  const hoyMismo = new Date().toISOString().slice(0, 10);
  const [fechaPago, setFechaPago] = useState(hoyMismo);
  const [fechaEmision, setFechaEmision] = useState(hoyMismo);
  const [confirmando, setConfirmando] = useState(false);
  const [cobrando, setCobrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultados, setResultados] = useState<Resultado[] | null>(null);
  const enviado = useRef(false);

  /**
   * Busca en el almacén local: los alumnos ya están en el dispositivo.
   *
   * Depende SOLO de lo tecleado. Antes también dependía de la lista de alumnos
   * elegidos, y como esa lista cambia de identidad cada vez que un calendario
   * emite su selección, la búsqueda se relanzaba sola y borraba las sugerencias
   * a media escritura: parecía que «se quitaba el filtro».
   */
  useEffect(() => {
    const termino = normalizeForSearch(busqueda.trim());
    if (termino.length < 2) {
      setSugerencias([]);
      return;
    }
    let vigente = true;
    void (async () => {
      const todos = await db.students.toArray();
      if (!vigente) return;
      setSugerencias(
        todos
          .filter(
            (alumno) =>
              alumno.searchName.includes(termino) ||
              alumno.code.toUpperCase().includes(busqueda.trim().toUpperCase()),
          )
          .slice(0, 12),
      );
    })();
    return () => {
      vigente = false;
    };
  }, [busqueda]);

  /**
   * Se añade SIN borrar lo escrito.
   *
   * Quien cobra a tres hermanos teclea el apellido una vez y quiere pulsar los
   * tres. Limpiar la caja al primero obligaba a volver a escribirlo cada vez, que
   * es justo lo que se reportó.
   */
  const anadir = (alumno: LocalStudent): void => {
    setRenglones((previos) =>
      previos.some((r) => r.alumno.id === alumno.id)
        ? previos
        : [...previos, { alumno, seleccion: SELECCION_VACIA }],
    );
    setResultados(null);
  };

  const quitar = (id: string): void => {
    setRenglones((previos) => previos.filter((r) => r.alumno.id !== id));
    setResultados(null);
  };

  const yaElegidos = new Set(renglones.map((r) => r.alumno.id));
  // Los ya elegidos se descartan AQUÍ, al pintar, y no dentro de la búsqueda:
  // así elegir a uno no obliga a relanzarla.
  const disponibles = sugerencias.filter((a) => !yaElegidos.has(a.id));

  /**
   * `useCallback` con dependencias vacías: el calendario la recibe como prop y la
   * tiene entre las dependencias de su emisión. Recrearla en cada render lo hacía
   * emitir otra vez, que cambiaba la lista, que provocaba otro render… un bucle.
   */
  const anotarSeleccion = useCallback(
    (id: string, seleccion: SeleccionCalendario): void => {
      setRenglones((previos) =>
        previos.map((r) => (r.alumno.id === id ? { ...r, seleccion } : r)),
      );
    },
    [],
  );

  const conCobro = renglones.filter((r) => r.seleccion.totalMinor > 0);

  /*
   * El 10% de hermanos: solo si son DOS O MÁS y cada uno paga su año completo.
   *
   * Con uno solo le toca el 7%, y esa rebaja se concede en su propia pantalla;
   * aquí sería dar el 10% a quien no le corresponde. Y si uno de los hermanos
   * paga el año y el otro tres meses, tampoco: la condición del colegio es que
   * se pague el de todos.
   */
  const descuentos = new Map(
    conCobro.map((r) => [
      r.alumno.id,
      descuentoAnualDisponible(r.seleccion, DESCUENTO_ANUAL_HERMANOS),
    ]),
  );
  const todosPaganElAño =
    conCobro.length >= 2 &&
    conCobro.every((r) => descuentos.get(r.alumno.id) != null);
  const descuentoDe = (id: string) =>
    todosPaganElAño ? (descuentos.get(id) ?? null) : null;

  const totalMinor = conCobro.reduce(
    (acc, r) =>
      acc +
      r.seleccion.totalMinor -
      (descuentoDe(r.alumno.id)?.totalMinor ?? 0),
    0,
  );
  const rebajaTotal = conCobro.reduce(
    (acc, r) => acc + (descuentoDe(r.alumno.id)?.totalMinor ?? 0),
    0,
  );
  const moneda = conCobro[0]?.seleccion.currency ?? "GTQ";

  const referenciaRequerida = metodo !== "EFECTIVO";
  const puedeCobrar =
    conCobro.length > 0 &&
    props.conexion === "EN_LINEA" &&
    (!referenciaRequerida || referencia.trim() !== "");

  const cobrar = async (): Promise<void> => {
    if (enviado.current) return;
    enviado.current = true;
    setCobrando(true);
    setError(null);

    // El instante REAL del registro. Las fechas que el cajero elige son de
    // presentación y no mueven ni la mora ni la fecha operativa del pago.
    const ahora = new Date().toISOString();
    const obtenidos: Resultado[] = [];

    /*
     * Se cobra alumno por alumno, y un fallo NO detiene a los demás.
     *
     * Es la decisión del colegio y tiene sentido en la ventanilla: el encargado
     * ya entregó el dinero, y devolvérselo entero porque a uno de los hermanos
     * le habían cobrado ese mes desde otra caja es peor que cobrarle lo que sí
     * correspondía. Lo que no puede pasar es que el fallo quede callado, así que
     * abajo se dice alumno por alumno qué entró y qué no.
     */
    for (const renglon of conCobro) {
      try {
        const cargos: LocalCharge[] = await db.charges
          .where("studentId")
          .equals(renglon.alumno.id)
          .toArray();

        const resultado = await registerPayment({
          session: props.session,
          student: renglon.alumno,
          charges: cargos,
          amountMinor:
            renglon.seleccion.totalMinor -
            (descuentoDe(renglon.alumno.id)?.totalMinor ?? 0),
          method: metodo,
          reference: referencia.trim() === "" ? null : referencia.trim(),
          allocations: distribucionExacta(
            renglon.seleccion,
            descuentoDe(renglon.alumno.id),
          ),
          annualDiscount: descuentoDe(renglon.alumno.id),
          monthlyStates: estadosDeMora(renglon.seleccion),
          cycleName: renglon.seleccion.cycleName,
          monthLabels: etiquetasDeMes(renglon.seleccion),
          issuedAt: fechaEmision,
          paidOn: fechaPago,
          note: nota.trim() === "" ? null : nota.trim(),
          // El cobro es exactamente lo marcado, así que nunca hay excedente.
          allowOverpayment: false,
          now: ahora,
        });

        obtenidos.push({
          alumno: renglon.alumno.fullName,
          paymentId: resultado.paymentId,
          recibo: resultado.receipt,
          problema: null,
        });
      } catch (problema) {
        obtenidos.push({
          alumno: renglon.alumno.fullName,
          paymentId: null,
          recibo: null,
          problema:
            problema instanceof DomainError
              ? problema.code === "CARGO_NO_ENCONTRADO"
                ? "Este cobro todavía no está en esta computadora. Pulse Sincronizar."
                : problema.message
              : "No fue posible registrar este cobro.",
        });
      }
    }

    setResultados(obtenidos);
    const cobrados = obtenidos.filter((r) => r.paymentId !== null);

    if (cobrados.length > 0) {
      try {
        /*
         * El comprobante se arma AQUÍ, no se le pide al servidor.
         *
         * Los cobros acaban de nacer y todavía no se han sincronizado, así que
         * el servidor no los conoce y devolvía 404: caja se quedaba sin papel
         * con el dinero ya recibido. Es el mismo criterio del recibo individual,
         * que también se imprime en el dispositivo antes de sincronizar.
         */
        const archivo = await renderConsolidatedLocalReceipt(
          cobrados
            .map((r) => r.recibo)
            .filter((recibo): recibo is ReceiptData => recibo !== null),
        );
        openPdf(archivo.receiptBytes, archivo.fileName);
      } catch {
        // El cobro YA quedó registrado; lo que falló es el papel. Decirlo así
        // evita que caja vuelva a cobrar creyendo que no se hizo.
        setError(
          "Los cobros se registraron, pero no se pudo generar el comprobante. " +
            "Sincronice y obténgalo desde «Comprobante consolidado».",
        );
      }
      await props.onCambio();
    }

    setRenglones((previos) =>
      previos.filter(
        (r) =>
          !cobrados.some((cobrado) => cobrado.alumno === r.alumno.fullName),
      ),
    );
    setConfirmando(false);
    setCobrando(false);
    enviado.current = false;
  };

  const importeLegible = useMemo(
    () => formatMoney(fromRow(totalMinor, moneda)),
    [totalMinor, moneda],
  );

  return (
    <section className="tarjeta" aria-label="Cobro a varios alumnos">
      <h2>Cobro a hermanos</h2>
      <p className="ayuda">
        Elija a los alumnos, marque lo que se le cobra a cada uno y confirme una
        sola vez. Cada alumno conserva su propio recibo —los saldos son
        individuales— y se entrega un solo comprobante con todo.
      </p>

      {props.conexion !== "EN_LINEA" && (
        <p className="aviso-error" role="status">
          Este cobro necesita conexión: el calendario y el comprobante los
          calcula el servidor. Sin internet, cobre a cada alumno desde su ficha.
        </p>
      )}

      <label className="campo">
        Añadir alumno por código o nombre
        <input
          type="text"
          value={busqueda}
          onChange={(evento) => setBusqueda(evento.target.value)}
          autoComplete="off"
        />
      </label>

      {disponibles.length > 0 && (
        <ul className="sugerencias">
          {disponibles.map((alumno) => (
            <li key={alumno.id}>
              <button
                type="button"
                className="boton"
                onClick={() => anadir(alumno)}
              >
                {alumno.code} · {alumno.fullName}
              </button>
            </li>
          ))}
        </ul>
      )}

      {renglones.length === 0 && (
        <p className="vacio">Todavía no ha elegido a ningún alumno.</p>
      )}

      {renglones.map((renglon) => (
        <article key={renglon.alumno.id} className="renglon-familiar">
          <header className="renglon-encabezado">
            <div>
              <strong>{renglon.alumno.fullName}</strong>
              <span className="tenue">
                {" "}
                · {renglon.alumno.code} · {renglon.alumno.gradeName}{" "}
                {renglon.alumno.sectionName}
              </span>
            </div>
            <button
              type="button"
              className="boton compacto"
              onClick={() => quitar(renglon.alumno.id)}
            >
              Quitar
            </button>
          </header>

          {props.conexion === "EN_LINEA" && renglon.alumno.cycleId !== "" && (
            <CalendarioDelAlumno
              session={props.session}
              alumno={renglon.alumno}
              conexion={props.conexion}
              anotar={anotarSeleccion}
              recarga={props.tickSync}
            />
          )}
        </article>
      ))}

      {conCobro.length > 0 && (
        <div className="resumen-familiar">
          <h3>
            Se va a cobrar a {conCobro.length} alumno(s) · {importeLegible}
          </h3>
          <div className="tabla-desplazable">
            <table className="tabla">
              <caption className="oculto-visual">
                Lo que se le cobra a cada alumno
              </caption>
              <thead>
                <tr>
                  <th scope="col">Alumno</th>
                  <th scope="col">Marcado</th>
                  <th scope="col" className="numero">
                    Importe
                  </th>
                </tr>
              </thead>
              <tbody>
                {conCobro.map((renglon) => (
                  <tr key={renglon.alumno.id}>
                    <th scope="row">{renglon.alumno.fullName}</th>
                    <td>
                      {renglon.seleccion.elegidos.length} mes(es)
                      {renglon.seleccion.otrosElegidos.length > 0 &&
                        ` y ${renglon.seleccion.otrosElegidos.length} concepto(s)`}
                    </td>
                    <td className="numero">
                      {formatMoney(
                        fromRow(
                          renglon.seleccion.totalMinor,
                          renglon.seleccion.currency,
                        ),
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <label className="campo">
            Fecha de emisión del comprobante
            <input
              type="date"
              value={fechaEmision}
              onChange={(evento) => setFechaEmision(evento.target.value)}
            />
          </label>

          <label className="campo">
            Fecha del cobro
            <input
              type="date"
              value={fechaPago}
              onChange={(evento) => setFechaPago(evento.target.value)}
              max={hoyMismo}
            />
          </label>
          {fechaPago !== hoyMismo && (
            <p className="ayuda">
              El comprobante se imprimirá con fecha <strong>{fechaPago}</strong>
              . Es solo lo que se lee en el papel: los pagos quedan registrados
              hoy y la mora es la de hoy.
            </p>
          )}

          <label className="campo">
            Método de pago
            <select
              value={metodo}
              onChange={(evento) =>
                setMetodo(evento.target.value as PaymentMethod)
              }
            >
              {METODOS.map((item) => (
                <option key={item} value={item}>
                  {PAYMENT_METHOD_LABELS[item]}
                </option>
              ))}
            </select>
          </label>

          {referenciaRequerida && (
            <label className="campo">
              Referencia (boleta, autorización)
              <input
                type="text"
                value={referencia}
                onChange={(evento) => setReferencia(evento.target.value)}
              />
            </label>
          )}

          <label className="campo">
            Observación (opcional)
            <input
              type="text"
              value={nota}
              onChange={(evento) => setNota(evento.target.value)}
            />
          </label>

          {error !== null && (
            <p className="aviso-error" role="alert">
              {error}
            </p>
          )}

          {!confirmando ? (
            <button
              type="button"
              className="boton primario"
              disabled={!puedeCobrar}
              onClick={() => setConfirmando(true)}
            >
              Cobrar {importeLegible} a {conCobro.length} alumno(s)
            </button>
          ) : (
            <div className="confirmacion">
              <p>
                Se registrarán {conCobro.length} cobros por {importeLegible} en
                total, y se entregará un solo comprobante.
              </p>
              {rebajaTotal > 0 && (
                <p className="ok">
                  <strong>
                    Incluye el descuento del {DESCUENTO_ANUAL_HERMANOS}% por
                    pago anual de hermanos:
                  </strong>{" "}
                  {formatMoney(fromRow(rebajaTotal, moneda))} menos entre los{" "}
                  {conCobro.length}. Sale en el comprobante, en las líneas de
                  cada uno.
                </p>
              )}
              <button
                type="button"
                className="boton primario"
                disabled={cobrando}
                onClick={() => void cobrar()}
              >
                {cobrando ? "Cobrando…" : "Sí, registrar los cobros"}
              </button>
              <button
                type="button"
                className="boton"
                disabled={cobrando}
                onClick={() => setConfirmando(false)}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}

      {resultados !== null && (
        <div className="resultado-familiar">
          <h3>Resultado</h3>
          <ul className="puntos">
            {resultados.map((resultado) => (
              <li key={resultado.alumno}>
                <strong>{resultado.alumno}</strong>:{" "}
                {resultado.paymentId !== null ? (
                  "cobrado"
                ) : (
                  <span className="marca-estado error">
                    no se cobró — {resultado.problema}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * El calendario de UN alumno dentro del cobro conjunto.
 *
 * Existe solo para tener aquí el `useCallback`. Antes se pasaba una flecha en
 * línea —`(s) => anotar(id, s)`— y esa flecha nace distinta en cada render; el
 * calendario la tiene entre las dependencias de su emisión, así que volvía a
 * emitir, lo que cambiaba la lista, lo que provocaba otro render. Un bucle que
 * en pantalla se veía como que la búsqueda «se borraba sola».
 */
function CalendarioDelAlumno(props: {
  session: StoredSession;
  alumno: LocalStudent;
  conexion: string;
  anotar: (id: string, seleccion: SeleccionCalendario) => void;
  recarga: number;
}): JSX.Element {
  const { anotar, alumno } = props;
  const alSeleccionar = useCallback(
    (seleccion: SeleccionCalendario) => anotar(alumno.id, seleccion),
    [anotar, alumno.id],
  );

  return (
    <StudentCalendar
      session={props.session}
      studentId={alumno.id}
      cycleId={alumno.cycleId}
      conexion={props.conexion}
      onSeleccionPago={alSeleccionar}
      recarga={props.recarga}
    />
  );
}
